/**
 * Company vocabulary (SOUND-296).
 *
 * The ONE definition of the tier and feature enums. Before this module the
 * tier lists lived in three places — `pages/companies/tabs/OverviewTab.jsx`
 * (both lists), the now-retired `CreateParentCompanyModal` (the client list
 * only) and `config/searchConfig.js` (a union of all six) — and the copies had
 * already drifted: no create surface could produce `Sourcing`, which is the
 * tier Media Resellers actually carries.
 *
 * Same lesson as `utilities/statusTones.js`. Import from here; do not
 * re-declare.
 *
 * The tier vocabulary is split BY COMPANY TYPE. A partner is never on a
 * merchant tier and a client is never on a referral tier, so always select
 * with `tierOptionsFor()` rather than offering the union.
 */

/** Tiers for `companyType: "client"` (including parent/holding companies). */
export const CLIENT_TIER_OPTIONS = [
  "Merchant Services",
  "Merchant Plus",
  "Managed Services",
];

/**
 * The one tier a child company inherits from its parent (SOUND-795, CPO
 * 2026-11-03). A child of a Managed Services parent DEFAULTS to Managed
 * Services; no other tier inherits. Rules live in `utilities/tierInheritance`.
 */
export const MANAGED_SERVICES_TIER = "Managed Services";

/** Tiers for `companyType: "partner"`. */
export const PARTNER_TIER_OPTIONS = ["Sourcing", "Referral", "ISV"];

/**
 * Every tier value, client tiers first. Only for surfaces that filter across
 * company types (the /companies search drawer) — never for an edit or create
 * control, which must narrow by type.
 */
export const ALL_TIER_OPTIONS = [
  ...CLIENT_TIER_OPTIONS,
  ...PARTNER_TIER_OPTIONS,
];

/**
 * Tier options for a company type. Anything that is not `partner` gets the
 * client tiers — `client` is the default company type, and `processor` /
 * `internal` companies have no tier vocabulary of their own.
 *
 * @param {string} companyType
 * @returns {string[]}
 */
export function tierOptionsFor(companyType) {
  return companyType === "partner" ? PARTNER_TIER_OPTIONS : CLIENT_TIER_OPTIONS;
}

/** `soundApp.companies.features` — checked by `requireFeature()` in sound-api. */
export const FEATURE_OPTIONS = [
  "data-pipeline",
  "mid-manager",
  "rerunner",
  "disputifier",
  "credential-management",
  "application-pipeline",
];

/** Company account status. */
export const COMPANY_STATUS_OPTIONS = ["pending", "active", "inactive"];
